import { Card, CardContent } from "../ui/card"
import { Button } from "../ui/button"
import { Trophy, Star, Crown, Globe } from "lucide-react"
import { useState } from "react"

const categoryConfig = {
  'jugadores-iconos': {
    icon: Crown,
    accent: '#8B7355',
    gradient: 'from-amber-100/30 to-yellow-100/20',
    badge: 'LEYENDAS',
    // cta: 'Ver Leyendas',
    cta: 'Proximamente',
    bgColor: 'bg-amber-50/80'
  },
  'balon-oro': {
    icon: Trophy,
    accent: '#D4A574',
    gradient: 'from-yellow-100/30 to-amber-100/20',
    badge: 'ELITE',
    // cta: 'Ver Ganadores',
    cta: 'Proximamente',
    bgColor: 'bg-yellow-50/80'
  },
  'champions': {
    icon: Star,
    accent: '#6B5B73',
    gradient: 'from-blue-100/20 to-indigo-100/20',
    badge: 'EUROPA',
    // cta: 'Ver Champions',
    cta: 'Proximamente',
    bgColor: 'bg-blue-50/60'
  },
  'mundial': {
    icon: Globe,
    accent: '#7B8B6F',
    gradient: 'from-green-100/20 to-emerald-100/20',
    badge: 'MUNDIAL',
    // cta: 'Ver Selecciones',
    cta: 'Proximamente',
    bgColor: 'bg-green-50/60'
  }
}

export default function EnhancedLegacyCard({ 
  image, 
  title, 
  description, 
  category = 'jugadores-iconos',
  itemCount,
  trending = false,
  onClick 
}) {
  const [isHovered, setIsHovered] = useState(false)
  const config = categoryConfig[category]
  const IconComponent = config.icon

  const handleClick = () => {
    if (onClick) {
      onClick(category)
    }
  }

  return (
    <Card 
      className={`overflow-hidden bg-white border border-gray-200/60 group cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-gray-300/25 ${config.bgColor}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <CardContent className="p-0 relative">
        
        {/* Trending Badge */}
        {trending && (
          <div className="absolute top-3 left-3 z-20">
            <div className="bg-amber-500 text-white text-xs px-2 py-1 rounded-md font-medium shadow-sm">
              ⭐ Destacado
            </div>
          </div>
        )}
        
        {/* Category Badge */}
        <div className="absolute top-3 right-3 z-20">
          <div 
            className="text-white text-xs px-3 py-1 rounded-md font-medium backdrop-blur-sm shadow-sm"
            style={{ 
              backgroundColor: config.accent,
            }}
          >
            {config.badge}
          </div>
        </div>

        {/* Image Container */}
        <div className="relative h-[200px] overflow-hidden">
          <img
            src={image}
            alt={title}
            className={`w-full h-full object-cover transition-all duration-500 ${
              isHovered ? 'scale-105' : 'scale-100'
            }`}
          />
          
          {/* Soft Overlay */}
          <div className={`absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent transition-opacity duration-300 ${
            isHovered ? 'opacity-60' : 'opacity-40'
          }`} />
          
          {/* Accent Gradient */}
          <div 
            className={`absolute inset-0 bg-gradient-to-br ${config.gradient} transition-opacity duration-300 ${
              isHovered ? 'opacity-30' : 'opacity-0'
            }`}
          />

          {/* Floating Icon */}
          <div className={`absolute bottom-4 right-4 transition-all duration-300 ${
            isHovered ? 'scale-110 -translate-y-1' : 'scale-100'
          }`}>
            <div 
              className="p-2 rounded-full backdrop-blur-sm shadow-lg"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              }}
            >
              <IconComponent 
                className="w-4 h-4" 
                style={{ color: config.accent }}
              />
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-4 relative bg-white">
          
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-gray-900 font-semibold text-lg leading-tight">
              {title}
            </h3>
          </div>
          
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            {description}
          </p>
          
          {/* Item Count */}
          {itemCount && (
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: config.accent }} />
              <span className="text-gray-500 text-xs">{itemCount} productos disponibles</span>
            </div>
          )}

          {/* Accent Line */}
          <div 
            className={`h-0.5 rounded-full mb-4 transition-all duration-300 ${
              isHovered ? 'w-8' : 'w-12'
            }`}
            style={{ backgroundColor: config.accent }}
          />

          {/* CTA Button */}
          <Button
            variant="outline"
            size="sm"
            className={`w-full text-gray-700 border border-gray-300 bg-white hover:bg-gray-50 transition-all duration-200 text-sm font-medium ${
              isHovered ? 'shadow-md' : ''
            }`}
            style={{
              '--hover-border': config.accent,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = config.accent
              e.currentTarget.style.color = config.accent
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgb(209 213 219)'
              e.currentTarget.style.color = 'rgb(55 65 81)'
            }}
          >
            <span className="flex items-center justify-center gap-2">
              {config.cta}
              <IconComponent className="w-3 h-3" />
            </span>
          </Button>
        </div>

        {/* Soft Glow Effect */}
        <div 
          className={`absolute inset-0 rounded-lg transition-opacity duration-300 pointer-events-none ${
            isHovered ? 'opacity-5' : 'opacity-0'
          }`}
          style={{
            background: `radial-gradient(circle at center, ${config.accent}40 0%, transparent 70%)`,
          }}
        />
      </CardContent>
    </Card>
  )
}